// components/GoogleSignIn.js
import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useAuth} from "../useAuth";
import axios from "axios";
import {Panel} from "primereact/panel";
import {RadioButton} from "primereact/radiobutton";
import {Card} from "primereact/card";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import {Dropdown} from "primereact/dropdown";

declare global {
    interface Window {
        idTokenHandlerGCP: (response: { credential: any }) => void;
        google: any;
    }
}

const GoogleSignIn = () => {

    const navigate = useNavigate();

    const {
        setGoogleIdToken
    } = useAuth();

    const [registrationType, setRegistrationType] = useState('newUserNewOrg');
    const [orgName, setOrgName] = useState('');
    const [orgDescription, setOrgDescription] = useState('');
    const [monthlyQuota, setMonthlyQuota] = useState<number | null>(10000);
    const [countryCode, setCountryCode] = useState<{ name: string, code: string }>();
    const [accessPoint, setAccessPoint] = useState<{ name: string, code: string }>();
    const [userInputOrgAccountId, setUserInputOrgAccountId] = useState('');

    const [countryCodes, setCountryCodes] = useState([]);

    const accessPoints = [
        {name: 'US', code: 'US'},
        {name: 'EU', code: 'EU'}
    ];

    useEffect(() => {
        localStorage.setItem('registrationType', registrationType);
    }, [registrationType]);

    useEffect(() => {
        localStorage.setItem('orgName', orgName);
    }, [orgName]);

    useEffect(() => {
        localStorage.setItem('orgDescription', orgDescription);
    }, [orgDescription]);

    useEffect(() => {
        localStorage.setItem('monthlyQuota', monthlyQuota !== null ? monthlyQuota.toString() : '');
    }, [monthlyQuota]);

    useEffect(() => {
        localStorage.setItem('countryCode', countryCode ? countryCode.code : '');
    }, [countryCode]);

    useEffect(() => {
        localStorage.setItem('accessPoint', accessPoint ? accessPoint.code : '');
    }, [accessPoint]);

    useEffect(() => {
        localStorage.setItem('userInputOrgAccountId', userInputOrgAccountId);
    }, [userInputOrgAccountId]);

    useEffect(() => {
        localStorage.setItem('orgName', orgName);
    }, [orgName]);


    const getCountryCodes = async () => {
        console.log("About to get country codes.");
        axios.get('https://restcountries.com/v3.1/all?fields=name,cca2') // or whatever your endpoint is
            .then(res => {
                console.log("Response from country codes API:", res.data);
                const formattedCountries = res.data.map((country: { name: { common: any; }; cca2: any; }) => ({
                    name: country.name.common,
                    code: country.cca2,
                }))
                    .sort((a: { name: string; }, b: { name: any; }) => a.name.localeCompare(b.name));
                console.log("Converted country codes response into formattedCountries:", formattedCountries);
                setCountryCodes(formattedCountries);
            })
            .catch(err => console.error(err));
    }

    const isFormValid = () => {
        if (registrationType === 'newUserNewOrg') {
            return orgName.trim() !== '' &&
                countryCode !== undefined &&
                accessPoint !== undefined;
        } else if (registrationType === 'newUserExistingOrg') {
            return userInputOrgAccountId.trim() !== '';
        }
        return false;
    };

    // Disable the Google Button if the form is not valid.
    useEffect(() => {
        const googleButton = document.getElementById("googleSignInButton");
        if (!isFormValid()) {
            googleButton?.classList.add("disabled-button");
        } else {
            googleButton?.classList.remove("disabled-button");
        }
    }, [orgName, userInputOrgAccountId, accessPoint, countryCode]);

    // Setup and Enable the Google Button
    useEffect(() => {

        getCountryCodes().then(r => console.log("Got the country codes"));

        // Load the Google script
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);

        // Define the callback globally
        window.idTokenHandlerGCP = (response: { credential: any; }) => {
            console.log("Google ID token response:", response);
            const token = response.credential;

            if (!token) {
                console.error("No token received");
                return;
            }

            setGoogleIdToken(token);
            navigate("/redirected");
        };

        // Wait a bit for the script to load and then initialize the button
        script.onload = () => {
            if (window.google) {

                window.google.accounts.id.initialize({
                    client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                    callback: window.idTokenHandlerGCP,
                    ux_mode: "popup",
                });

                window.google.accounts.id.renderButton(
                    document.getElementById("googleSignInButton"),
                    {
                        theme: "outline",
                        size: "medium",
                        text: "signin_with",
                        shape: "rectangular",
                        logo_alignment: "left",
                    }
                );
            }
        };
    }, []);

    return (
        <div>
            <div className="menuHeaderContainer mb-3">
                <img src="/favicon.svg" alt="Objects Grid" className="menuHeaderImage"/>
                <div className="menuHeaderText">OBJECTS GRID</div>
            </div>
            <Panel header="Are you registering a new org or loging into an an exsting account?" className="m-2">
                <div>
                    <div className="flex align-items-center mb-2">
                        <RadioButton
                            inputId="newUserNewOrg"
                            name="registrationType"
                            value="newUserNewOrg"
                            onChange={(e) => setRegistrationType(e.value)}
                            checked={registrationType === 'newUserNewOrg'}/>
                        <label
                            htmlFor="newUserNewOrg"
                            className="ml-2">Create a <b>New</b> Organization Account with yourself as
                            administrator.</label>
                    </div>
                    <div>
                        <RadioButton
                            inputId="newUserExistingOrg"
                            name="registrationType"
                            value="newUserExistingOrg"
                            onChange={(e) => setRegistrationType(e.value)}
                            checked={registrationType === 'newUserExistingOrg'}/>
                        <label
                            htmlFor="newUserExistingOrg"
                            className="ml-2">Log me in, or create me as a new user for
                            an <b>Existing</b> Organization Account</label>
                    </div>
                </div>
            </Panel>

            {registrationType === 'newUserNewOrg' && (
                <Panel header="Create a New Organization Account" className="m-2">
                    <p>After giving your Org a name and defining from where the client will access it, use your Google
                        account to <b> register a
                            new Organisation Account with ObjectsGrid</b>. You will be given a new Organisation Account
                        ID that you are an
                        administrator of. You will use this Organisation Account ID for future ObjectsGrid API calls.
                    </p>
                    <div className="flex flex-column mb-2 gap-2">
                        <label htmlFor="orgName"><b>Organization Name</b></label>
                        <InputText id="orgName"
                                   aria-describedby="orgname-help"
                                   onChange={(e) => setOrgName(e.target.value)}
                                   className={orgName.trim() === '' ? 'p-invalid' : ''}/>
                        <small id="orgname-help">
                            [Required] The name of the organization that will be created in ObjectsGrid.
                        </small>
                    </div>
                    <div className="flex flex-column mb-2 gap-2">
                        <label htmlFor="orgDescription"><b>Organization Description</b></label>
                        <InputText id="orgDescription"
                                   aria-describedby="orgdesc-help"
                                   onChange={(e) => setOrgDescription(e.target.value)}/>
                        <small id="orgdesc-help">
                            [Optional] A description of your organization that will be created in
                            ObjectsGrid.
                        </small>
                    </div>
                    <div className="flex flex-column mb-2 gap-2">
                        <label htmlFor="monthlyQuota"><b>Monthly Quota</b></label>
                        <InputNumber id="monthlyQuota"
                                     value={monthlyQuota}
                                     aria-describedby="monthlyquota-help"
                                     onValueChange={(e) => setMonthlyQuota(e.target.value ?? null)}/>
                        <small id="monthlyquota-help">
                            [Optional] The max number of API calls in a month
                        </small>
                    </div>
                    <div className="flex flex-column mb-2 gap-2">
                        <label htmlFor="orgDescription"><b>Country Code</b></label>
                        <Dropdown value={countryCode}
                                  filter
                                  onChange={(e) => setCountryCode(e.value)}
                                  options={countryCodes}
                                  optionLabel="name"
                                  placeholder="Select a Country Code"
                                  className="w-full md:w-14rem"
                        />
                        <small id="accesspoint-help">
                            [Required] Where is your organization headquartered?
                        </small>
                    </div>
                    <div className="flex flex-column  mb-2 gap-2">
                        <label htmlFor="orgDescription"><b>Access Point</b></label>
                        <Dropdown value={accessPoint}
                                  onChange={(e) => setAccessPoint(e.value)}
                                  options={accessPoints}
                                  optionLabel="name"
                                  placeholder="Select an Access Point"
                                  className="w-full md:w-14rem"
                        />
                        <small id="accesspoint-help">
                            [Required] Where will you API call's mostly be made from?
                        </small>
                    </div>
                </Panel>
            )}

            {registrationType === 'newUserExistingOrg' && (
                <Panel header="Register or get Token for a User With an Exsting Organization Account"
                       className="m-2">
                    <p>Use your Google Account to login to an existing account. If your email
                        address is not already registered with this organization account, it will be added.</p>
                    <div className="flex flex-column gap-2">
                        <label htmlFor="orgAccountId"><b>Organization Account Id</b></label>
                        <InputText id="orgAccountId"
                                   aria-describedby="orgaccountid-help"
                                   placeholder="org-123-4567"
                                   onChange={(e) => setUserInputOrgAccountId(e.target.value)}
                        />
                        <small id="orgaccountid-help">
                            [Required] The ObjectsGrid Organization Account ID.
                        </small>
                    </div>
                </Panel>
            )}

            <div id="googleSignInButton"
                 style={{
                     display: "inline-block",
                     width: "auto",
                     minWidth: "240px",
                 }}
                 className="m-2"></div>
        </div>
    );
};

export default GoogleSignIn;
